import { useQuery } from '@tanstack/react-query';
import {
  CLAIM_STATUS_CATALOG_DEFAULT,
  localeSchema,
  type ClaimStatus,
  type ClaimStatusCatalog,
  type ClaimStatusCatalogEntry,
} from '@warehouse/shared';
import { useTranslation } from 'react-i18next';

import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { claimKeys, fetchClaimStatuses } from '../api/claims-api';

/**
 * The editable status catalog (labels + colours, `app_settings`), with the
 * seeded Swedish defaults as the offline fallback so a status pill never
 * renders as a raw enum key while the request is in flight.
 */
export function useClaimStatuses(): {
  statuses: ClaimStatusCatalog;
  entry: (key: ClaimStatus) => ClaimStatusCatalogEntry;
  label: (key: ClaimStatus) => string;
  color: (key: ClaimStatus) => string;
  isLoading: boolean;
} {
  const { i18n } = useTranslation();
  const query = useQuery({
    queryKey: claimKeys.statuses(),
    queryFn: fetchClaimStatuses,
    staleTime: 60_000,
  });
  useLiveQuery(['claims'], claimKeys.statuses());

  const statuses = query.data?.statuses ?? CLAIM_STATUS_CATALOG_DEFAULT;
  const parsed = localeSchema.safeParse(i18n.language);
  const locale = parsed.success ? parsed.data : 'sv';

  const entry = (key: ClaimStatus): ClaimStatusCatalogEntry =>
    statuses.find((status) => status.key === key) ??
    CLAIM_STATUS_CATALOG_DEFAULT.find((status) => status.key === key) ??
    CLAIM_STATUS_CATALOG_DEFAULT[0]!;

  return {
    statuses,
    entry,
    // Swedish is the seeded label and the operating language: an empty
    // translation falls back to it rather than to the machine key.
    label: (key) => entry(key).labels[locale]?.trim() || entry(key).labels.sv,
    color: (key) => entry(key).color,
    isLoading: query.isLoading,
  };
}
